import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  Checkbox, CircularProgress, Chip, InputAdornment
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Visibility, CheckCircle, Warning, ListAlt, Search, List } from '@mui/icons-material';
import { fetchData } from '../../../apiService';

const PurchaseModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  purchaseDetails,
  setPurchaseDetails,
  onSave,
  onVerify,
  onToGoodReceipt,
  suppliers,
  items,
  loading,
  freightPackingGST,
  companyId,
  token,
  setLoading,
  setLoadingMessage
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  // VATCalculationType: 1 = Excluding Tax, 2 = Including Tax
  const [vatType, setVatType] = useState(formData.VATCalculationType || 1);
  
  // Vendor Items Modal State
  const [vendorItemsModalOpen, setVendorItemsModalOpen] = useState(false);
  const [vendorItems, setVendorItems] = useState([]);
  const [selectedVendorItems, setSelectedVendorItems] = useState([]);
  const [loadingVendorItems, setLoadingVendorItems] = useState(false);
  
  // Get Items modal state
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Purchase' : modalAction === 'edit' ? 'Edit Purchase' : 'View Purchase';

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
    }
  }, [open]);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') return 'Bill Number is required';
        return '';
      case 'BillDate':
        if (!value) return 'Bill Date is required';
        return '';
      case 'MasterId':
        if (!value) return 'Supplier is required';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    
    // Required field validations
    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;
    
    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;
    
    const supplierError = validateField('MasterId', formData.MasterId);
    if (supplierError) newErrors.MasterId = supplierError;

    // Purchase details validation
    if (purchaseDetails.length === 0) {
      newErrors.purchaseDetails = 'At least one item is required';
    } else {
      const detailErrors = [];
      purchaseDetails.forEach((detail, index) => {
        const itemErrors = {};
        if (!detail.ItemId) itemErrors.ItemId = 'Item is required';
        if (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) {
          itemErrors.ReceivedQuantity = 'Quantity must be greater than 0';
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.Rate = 'Rate must be greater than 0';
        }
        if (Object.keys(itemErrors).length > 0) {
          detailErrors[index] = itemErrors;
        }
      });
      if (detailErrors.length > 0) {
        newErrors.details = detailErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ BillNumber: true, BillDate: true, MasterId: true });
    
    // Build validation error messages
    const validationMessages = [];
    
    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push('Bill Number is required');
    }
    if (!formData.BillDate) {
      validationMessages.push('Bill Date is required');
    }
    if (!formData.MasterId) {
      validationMessages.push('Supplier is required');
    }
    if (purchaseDetails.length === 0) {
      validationMessages.push('At least one item is required');
    } else {
      // Check each item for Received Qty and Rate
      const itemErrors = [];
      purchaseDetails.forEach((detail, index) => {
        if (!detail.ItemId) {
          itemErrors.push(`Row ${index + 1}: Item is required`);
        }
        if (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) {
          itemErrors.push(`Row ${index + 1}: Received Qty is required`);
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.push(`Row ${index + 1}: Rate is required`);
        }
      });
      if (itemErrors.length > 0) {
        validationMessages.push(...itemErrors);
      }
    }
    
    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }
    
    if (validateForm()) {
      onSave();
    }
  };

  // Sync vatType with formData
  useEffect(() => {
    if (formData.VATCalculationType) {
      setVatType(formData.VATCalculationType);
    }
  }, [formData.VATCalculationType]);

  // Recalculate all items when VAT type changes
  useEffect(() => {
    if (purchaseDetails.length > 0) {
      const updatedDetails = purchaseDetails.map(detail => {
        const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
        const rate = Math.max(0, parseFloat(detail.Rate) || 0);
        const vatRate = Math.max(0, parseFloat(detail.VATRate) || 0);
        const discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
        
        let taxableAmount, vatAmount, amount, discountAmount;
        let baseAmount;
        
        if (vatType === 2) {
          // Including Tax - reverse calculate
          const totalWithVat = receivedQty * rate;
          baseAmount = totalWithVat / (1 + vatRate / 100);
        } else {
          // Excluding Tax
          baseAmount = receivedQty * rate;
        }
        
        discountAmount = (baseAmount * discountPct) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = receivedQty * rate;
        
        return {
          ...detail,
          Amount: Math.round(amount * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          VATAmount: Math.round(vatAmount * 100) / 100,
          TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
        };
      });
      
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [vatType]);

  const handleVatTypeChange = (value) => {
    const intValue = parseInt(value);
    setVatType(intValue);
    const vatTypeName = intValue === 2 ? 'Including Tax' : 'Excluding Tax';
    setFormData(prev => ({ ...prev, VATCalculationType: intValue, VATCalculationTypeName: vatTypeName }));
  };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }
    
    if (field === 'MasterId') {
      // Clear purchase items table when supplier changes
      setPurchaseDetails([]);
      
      // Fetch vendor details from API to get InvoiceTypeName
      const fetchVendorDetails = async () => {
        try {
          const response = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${value}`, token);
          const vendorData = response.response_data?.Ledger || response.response_data || response || {};
          
          const invoiceTypeName = vendorData.InvoiceTypeName || vendorData.InvoiceType || '';
          const vatNumber = vendorData.VATNumber || vendorData.GSTNumber || vendorData.GSTNo || '';
          const masterName = vendorData.Name || vendorData.CustomerName || '';
          
          setFormData(prev => ({ 
            ...prev, 
            [field]: value, 
            InvoiceTypeName: invoiceTypeName, 
            VATNumber: vatNumber,
            MasterName: masterName
          }));
        } catch (error) {
          console.error('Error fetching vendor details:', error);
          // Fallback to supplier list data if API fails
          const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(value));
          const invoiceTypeName = selectedSupplier?.InvoiceTypeName || selectedSupplier?.InvoiceType || '';
          const vatNumber = selectedSupplier?.VATNumber || selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || '';
          
          setFormData(prev => ({ ...prev, [field]: value, InvoiceTypeName: invoiceTypeName, VATNumber: vatNumber }));
        }
      };
      
      fetchVendorDetails();
    } else if (field === 'PaidAmount') {
      // Validate Paid Amount should not exceed Total Amount
      const paidValue = parseFloat(value) || 0;
      const totalAmount = parseFloat(formData.TotalAmount) || 0;
      if (paidValue > totalAmount) {
        showError('Paid Amount cannot be greater than Total Amount');
        return;
      }
      setFormData(prev => ({ ...prev, [field]: value }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const isIGSTInvoice = () => {
    const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
    return invoiceType.includes('igst');
  };

  // Fetch Vendor Items
  const fetchVendorItems = async (masterId) => {
    if (!masterId || !companyId || !token) return;
     
    setLoadingVendorItems(true);
    
    try {
      const response = await fetchData('PurchaseApi', `/VendorWiseItemDetails/${companyId}/${masterId}`, token);
      const vendorItemsData = response.response_data || response || [];
      setVendorItems(Array.isArray(vendorItemsData) ? vendorItemsData : []);
      setSelectedVendorItems([]);
      return true;
    } catch (error) {
      console.error('Error fetching vendor items:', error);
      setVendorItems([]);
      showError('Failed to fetch vendor items');
      return false;
    } finally {
      setLoadingVendorItems(false);
    }
  };

  // Open Vendor Items Modal
  const handleOpenVendorItemsModal = async () => {
    if (!formData.MasterId) {
      showError('Please select a supplier first');
      return;
    }
    
    // Open modal first, then fetch data
    setVendorItemsModalOpen(true);
    await fetchVendorItems(formData.MasterId);
  };

  // Fetch Vendor Items and pre-select items already in purchase table
  useEffect(() => {
    if (vendorItemsModalOpen && vendorItems.length > 0) {
      // Get item IDs that are already in the purchase table
      const existingItemIds = purchaseDetails.map(d => d.ItemId);
      // Pre-select items that are already in the table
      const preSelectedItems = vendorItems
        .filter(item => existingItemIds.includes(item.ItemId || item.Id))
        .map(item => item.ItemId || item.Id);
      setSelectedVendorItems(preSelectedItems);
    }
  }, [vendorItemsModalOpen, vendorItems, purchaseDetails]);

  // Close Vendor Items Modal
  const handleCloseVendorItemsModal = () => {
    setVendorItemsModalOpen(false);
    setVendorItems([]);
    setSelectedVendorItems([]);
  };

  // Toggle Vendor Item Selection
  const handleToggleVendorItem = (itemId) => {
    setSelectedVendorItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Select All Vendor Items
  const handleSelectAllVendorItems = () => {
    if (selectedVendorItems.length === vendorItems.length) {
      setSelectedVendorItems([]);
    } else {
      setSelectedVendorItems(vendorItems.map(item => item.ItemId || item.Id));
    }
  };

  // Add Selected Vendor Items to Purchase
  const handleAddVendorItemsToPurchase = () => {
    const itemsToAdd = vendorItems.filter(item => 
      selectedVendorItems.includes(item.ItemId || item.Id)
    );

    const newPurchaseDetails = itemsToAdd.map(item => {
      const itemId = item.ItemId || item.Id;
      
      // Use LastPurchaseRateByVendor if available and > 0, otherwise use Rate/SaleRate
      const lastRate = parseFloat(item.LastPurchaseRateByVendor || 0);
      const defaultRate = parseFloat(item.Rate || item.SaleRate || 0);
      const rate = lastRate > 0 ? lastRate : defaultRate;
      
      const vatRate = parseFloat(item.VATRate || item.GSTRate || item.GST || 0);
      const qty = 1; // Default quantity
      
      let amount, taxableAmount, vatAmount, totalAmount, discountAmount;
      const discountPct = 0;
      
      if (isIncludingTax()) {
        // Rate includes VAT - reverse calculate like PurchaseReturn
        const totalWithVat = qty * rate;
        const baseAmount = totalWithVat / (1 + vatRate / 100);
        discountAmount = (baseAmount * discountPct) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = totalWithVat;
        totalAmount = taxableAmount + vatAmount;
      } else {
        // Rate excludes VAT
        const baseAmount = qty * rate;
        discountAmount = (baseAmount * discountPct) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = baseAmount;
        totalAmount = taxableAmount + vatAmount;
      }

      return {
        Id: 0,
        ItemId: itemId,
        ItemName: item.ItemName || item.Name,
        HSNCode: item.HSNCode || item.HSN || '',
        ReceivedQuantity: qty,
        DamageQuantity: 0,
        StockQuantity: qty,
        Rate: rate,
        Amount: Math.round(amount * 100) / 100,
        DiscountPercentage: discountPct,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        VATRate: vatRate,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        VATAmount: Math.round(vatAmount * 100) / 100,
        TotalAmount: Math.round(totalAmount * 100) / 100
      };
    });

    // Add to existing purchase details (avoid duplicates)
    const existingItemIds = purchaseDetails.map(d => d.ItemId);
    const uniqueNewItems = newPurchaseDetails.filter(item => !existingItemIds.includes(item.ItemId));
    
    if (uniqueNewItems.length > 0) {
      const updatedDetails = [...purchaseDetails, ...uniqueNewItems];
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
      setSnackbar({ open: true, message: `${uniqueNewItems.length} item(s) added successfully`, severity: 'success' });
    }
    
    const duplicateCount = newPurchaseDetails.length - uniqueNewItems.length;
    if (duplicateCount > 0 && uniqueNewItems.length === 0) {
      setSnackbar({ open: true, message: `All selected items are already in the table`, severity: 'info' });
    } else if (duplicateCount > 0) {
      setSnackbar({ open: true, message: `${uniqueNewItems.length} item(s) added. ${duplicateCount} item(s) already exist`, severity: 'info' });
    }
    
    handleCloseVendorItemsModal();
  };

  // Get Items Modal Handlers
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      // Items are already loaded in the parent component
      console.log('Items already available:', items.length);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.Rate || item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Filter items based on search value for Get Items modal
  const getFilteredItems = () => {
    // First filter: Only show items where IsProduct is false (not a product)
    const nonProductItems = items.filter(item => item.IsProduct === false || item.IsProduct === 0 || !item.IsProduct);
    
    if (!getItemsSearchValue.trim()) {
      return nonProductItems;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return nonProductItems.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm) ||
      item.Code?.toLowerCase().includes(searchTerm)
    );
  };

  // Check if item is already in purchase details
  const isItemAlreadyInPurchase = (itemId) => {
    return purchaseDetails.some(detail => detail.ItemId === parseInt(itemId));
  };

  // Get paginated items for Get Items modal
  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    // Filter out items that are already in purchase details
    const notAddedItems = filtered.filter(item => !isItemAlreadyInPurchase(item.Id));
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return notAddedItems.slice(startIndex, endIndex);
  };

  const getTotalPages = Math.ceil(getFilteredItems().filter(item => !isItemAlreadyInPurchase(item.Id)).length / itemsPerPage);

  // Add selected items to purchase details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const useIGST = isIGSTInvoice();

      const itemsToAdd = selectedItems.filter(
        selectedItem => !isItemAlreadyInPurchase(selectedItem.Id)
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const newItems = itemsToAdd.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const vatRate = parseFloat(selectedItem.VATRate || selectedItem.GSTRate || selectedItem.GST) || 0;
        const discountPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
        const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
        
        let taxableAmount, vatAmount, totalAmount, amount, discountAmount, discountPercent;
        let baseAmount;
        
        if (isIncludingTax()) {
          // Rate includes VAT - reverse calculate like PurchaseReturn
          const totalWithVat = quantity * rate;
          baseAmount = totalWithVat / (1 + vatRate / 100);
        } else {
          // Rate excludes VAT
          baseAmount = quantity * rate;
        }
        
        // Handle discount calculation
        if (discountAmt > 0) {
          discountAmount = discountAmt;
          discountPercent = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
        } else {
          discountPercent = discountPct;
          discountAmount = (baseAmount * discountPercent) / 100;
        }
        
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = quantity * rate;
        totalAmount = taxableAmount + vatAmount;

        return {
          Id: 0,
          ItemId: selectedItem.Id,
          ItemName: selectedItem.Name,
          HSNCode: selectedItem.HSNCode || '',
          ReceivedQuantity: quantity,
          DamageQuantity: 0,
          StockQuantity: quantity,
          Rate: rate,
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPercent * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          VATRate: vatRate,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          VATAmount: Math.round(vatAmount * 100) / 100,
          TotalAmount: Math.round(totalAmount * 100) / 100
        };
      });

      const updatedDetails = [...purchaseDetails, ...newItems];
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
      setHasUnsavedChanges(true);
      handleCloseGetItemsModal();
      setSnackbar({ open: true, message: `${newItems.length} item(s) added successfully`, severity: 'success' });
    } catch (error) {
      console.error('Error adding items:', error);
      showError('Failed to add items');
    } finally {
      setAddingItemsLoading(false);
    }
  };

  const isIncludingTax = () => vatType === 2;

  const handleDetailChange = (index, field, value) => {
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }
    
    const updatedDetails = [...purchaseDetails];
    
    if (['ReceivedQuantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setPurchaseDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
    }
    
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['ReceivedQuantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'VATRate'].includes(field)) {
      const detail = updatedDetails[index];
      const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
      // In create/edit mode, stock quantity equals received quantity (damage/excess/less are set during verification)
      const stockQty = receivedQty;
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const vatRate = Math.max(0, parseFloat(detail.VATRate) || 0);

      let taxableAmount, vatAmount, amount, discountAmount, discountPercent;
      let baseAmount;
      
      if (isIncludingTax()) {
        // Rate includes VAT - reverse calculate like PurchaseReturn
        const totalWithVat = receivedQty * rate;
        baseAmount = totalWithVat / (1 + vatRate / 100);
      } else {
        // Rate excludes VAT
        baseAmount = receivedQty * rate;
      }
      
      // Handle discount calculation
      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, parseFloat(value) || 0);
        discountPercent = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
      } else {
        discountPercent = parseFloat(detail.DiscountPercentage) || 0;
        discountAmount = (baseAmount * discountPercent) / 100;
      }
      
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;
      amount = receivedQty * rate;
      
      updatedDetails[index] = {
        ...updatedDetails[index],
        StockQuantity: stockQty,
        BilledQuantity: receivedQty,
        OrderQuantity: receivedQty,
        Amount: Math.round(amount * 100) / 100,
        DiscountPercentage: Math.round(discountPercent * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        VATAmount: Math.round(vatAmount * 100) / 100,
        TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
      };
    }
    setPurchaseDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = (index, itemId) => {
    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...purchaseDetails];
      const receivedQty = parseFloat(updatedDetails[index].ReceivedQuantity) || 0;
      const rate = selectedItem.PurchaseRate || selectedItem.SaleRate || 0;
      const vatRate = selectedItem.VATRate || selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;

      let taxableAmount, vatAmount, amount, discountAmount;
      let baseAmount;

      if (isIncludingTax()) {
        // Rate includes VAT - reverse calculate like PurchaseReturn
        const totalWithVat = receivedQty * rate;
        baseAmount = totalWithVat / (1 + vatRate / 100);
        discountAmount = (baseAmount * discountPct) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = totalWithVat;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          HSNCode: selectedItem.HSNCode || '',
          VATRate: vatRate,
          Rate: rate,
          Amount: Math.round(amount * 100) / 100,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          VATAmount: Math.round(vatAmount * 100) / 100,
          TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
        };
      } else {
        // Rate excludes VAT
        baseAmount = receivedQty * rate;
        discountAmount = (baseAmount * discountPct) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = baseAmount;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          HSNCode: selectedItem.HSNCode || '',
          VATRate: vatRate,
          Rate: rate,
          Amount: Math.round(amount * 100) / 100,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          VATAmount: Math.round(vatAmount * 100) / 100,
          TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
        };
      }
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  };

  const addDetailRow = () => {
    setHasUnsavedChanges(true);
    setPurchaseDetails([...purchaseDetails, {
      Id: 0, ItemId: '', ItemName: '', BatchNumber: '', PurchaseOrderId: null,
      OrderQuantity: 0, ReceivedQuantity: 0, BilledQuantity: 0, 
      DamageQuantity: 0, ExcessQuantity: 0, LessQuantity: 0, StockQuantity: 0, 
      ReturnDamageQuantity: 0, ReturnStockQuantity: 0,
      Rate: 0, Amount: 0, DiscountPercentage: 0, DiscountAmount: 0,
      HSNCode: '', VATRate: 0, TaxableAmount: 0, VATAmount: 0, TotalAmount: 0, IsActive: true
    }]);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = purchaseDetails.filter((_, i) => i !== index);
    setPurchaseDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      VATAmount: acc.VATAmount + (parseFloat(d.VATAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, VATAmount: 0, TotalAmount: 0 });

    const defaultFreightVATRate = parseFloat(freightPackingGST?.freight?.VATRate || freightPackingGST?.freight?.GSTRate) || 0;
    const defaultPackingVATRate = parseFloat(freightPackingGST?.packing?.VATRate || freightPackingGST?.packing?.GSTRate) || 0;
    const maxItemVATRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.VATRate) || 0)) : 0;
    const effectiveFreightVATRate = Math.max(defaultFreightVATRate, maxItemVATRate);
    const effectivePackingVATRate = Math.max(defaultPackingVATRate, maxItemVATRate);

    const freightAmount = parseFloat(formData.FreightAmount) || 0;
    const freightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;
    const packingAmount = parseFloat(formData.PackingAmount) || 0;
    const packingVATAmount = (packingAmount * effectivePackingVATRate) / 100;
    const tcsAmount = parseFloat(formData.TCSAmount) || 0;
    const grandTotal = totals.TotalAmount + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const totalAmount = Math.round(grandTotal);
    const paidAmount = parseFloat(formData.PaidAmount) || 0;
    const balanceAmount = totalAmount - paidAmount;

    setFormData(prev => ({
      ...prev, ...totals,
      FreightVATRate: effectiveFreightVATRate,
      FreightVATAmount: freightVATAmount,
      PackingVATRate: effectivePackingVATRate,
      PackingVATAmount: packingVATAmount,
      RoundOff: roundOff,
      TotalAmount: totalAmount,
      BalanceAmount: balanceAmount
    }));
  };

  useEffect(() => {
    // Only auto-calculate totals in create mode
    if (!isViewMode && modalAction === 'create') {
      calculateTotals(purchaseDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, formData.TCSAmount, formData.PaidAmount]);

  // Recalculate totals in edit mode when freight/packing amounts change (user input)
  useEffect(() => {
    if (!isViewMode && modalAction === 'edit' && purchaseDetails.length > 0) {
      calculateTotals(purchaseDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, formData.TCSAmount, formData.PaidAmount]);

  useEffect(() => {
    if (!isViewMode && purchaseDetails.length > 0 && formData.InvoiceTypeName) {
      const useIGST = isIGSTInvoice();
      const updatedDetails = purchaseDetails.map(d => {
        const gstRate = parseFloat(d.GSTRate) || 0;
        const taxableAmount = parseFloat(d.TaxableAmount) || 0;
        const gstAmount = (taxableAmount * gstRate) / 100;
        return { ...d, CGSTAmount: useIGST ? 0 : gstAmount / 2, SGSTAmount: useIGST ? 0 : gstAmount / 2, IGSTAmount: useIGST ? gstAmount : 0 };
      });
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [formData.InvoiceTypeName]);

  const formatCurrency = (amt) => `AED ${(amt || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getSelectedSupplierVAT = () => {
    if (formData.VATNumber) return formData.VATNumber;
    if (!formData.MasterId) return null;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return supplier?.VATNumber || supplier?.GSTNumber || supplier?.GSTNo || null;
  };

  const getSelectedSupplierInvoiceType = () => {
    if (formData.InvoiceTypeName) return formData.InvoiceTypeName;
    if (!formData.MasterId) return null;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return supplier?.InvoiceTypeName || supplier?.InvoiceType || null;
  };

  const getSupplierDisplayName = () => {
    if (formData.MasterName) return formData.MasterName;
    if (!formData.MasterId) return '';
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return supplier?.Name || '';
  };

  const supplierVATNo = getSelectedSupplierVAT();
  const supplierInvoiceType = getSelectedSupplierInvoiceType();
  const supplierDisplayName = getSupplierDisplayName();

  const disabledFieldStyle = { '& .MuiInputBase-input.Mui-disabled': { color: '#7c3aed', fontWeight: 700, WebkitTextFillColor: '#7c3aed' } };
  const compactInput = { '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' }, ...(isViewMode && disabledFieldStyle) };

  return (
    <Dialog open={open} onClose={(e, reason) => { 
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (hasUnsavedChanges && !isViewMode) {
            setCancelConfirmOpen(true);
            return;
          }
        }
        onClose(); 
      }}
      maxWidth="xl" fullWidth fullScreen={isMobile} disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1400px' } }}>
      
      <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e9d5ff', bgcolor: '#faf5ff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart sx={{ color: '#7c3aed', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#6b21a8', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
          </Box>
          <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#fff' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#7c3aed', mb: 1, display: 'block' }}>Purchase Information</Typography>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={1.2}>
            <TextField 
              fullWidth 
              size="small" 
              label="Bill No *" 
              value={formData.BillNumber || ''}
              onChange={(e) => handleChange('BillNumber', e.target.value)} 
              onBlur={() => handleBlur('BillNumber')}
              disabled={isViewMode}
              error={touched.BillNumber && !!errors.BillNumber}
              helperText={touched.BillNumber && errors.BillNumber}
              sx={compactInput} 
            />
          </Grid>
          <Grid item xs={6} sm={1.2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Bill Date *" 
              value={formData.BillDate || ''}
              onChange={(e) => handleChange('BillDate', e.target.value)} 
              onBlur={() => handleBlur('BillDate')}
              disabled={isViewMode}
              error={touched.BillDate && !!errors.BillDate}
              helperText={touched.BillDate && errors.BillDate}
              InputLabelProps={{ shrink: true }} 
              sx={compactInput} 
            />
          </Grid>
          <Grid item xs={6} sm={2.2}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="Supplier" value={supplierDisplayName || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                <FormControl fullWidth size="small" error={touched.MasterId && !!errors.MasterId} sx={compactInput}>
                  <InputLabel>Supplier *</InputLabel>
                  <Select 
                    value={formData.MasterId || ''} 
                    onChange={(e) => handleChange('MasterId', e.target.value)} 
                    onBlur={() => handleBlur('MasterId')}
                    disabled={isViewMode} 
                    label="Supplier *" 
                    sx={{ fontSize: '0.8rem' }} 
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                  >
                    {suppliers.map(s => <MenuItem key={s.MasterId || s.Id} value={s.MasterId || s.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>{s.Name}</MenuItem>)}
                  </Select>
                  {touched.MasterId && errors.MasterId && <FormHelperText>{errors.MasterId}</FormHelperText>}
                </FormControl>
                {formData.MasterId && (
                  <IconButton 
                    size="small" 
                    onClick={handleOpenVendorItemsModal}
                    sx={{ 
                      mt: 0.5,
                      bgcolor: '#f3e8ff', 
                      color: '#7c3aed',
                      '&:hover': { bgcolor: '#e9d5ff' },
                      width: 32,
                      height: 32
                    }}
                    title="View Vendor Items"
                  >
                    <ListAlt fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={6} sm={1.8}>
            <TextField fullWidth size="small" label="VAT No" value={supplierVATNo || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierVATNo ? '#fef3c7' : '#f9fafb' } }} />
          </Grid>
          {/* <Grid item xs={6} sm={1.5}>
            <TextField fullWidth size="small" label="Inv Type" value={supplierInvoiceType || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierInvoiceType ? '#fef9c3' : '#f9fafb' } }} />
          </Grid> */}
          <Grid item xs={6} sm={1.5}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="VAT Type" value={vatType === 2 ? 'Including Tax' : 'Excluding Tax'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: '#f3e8ff' } }} />
            ) : (
              <FormControl fullWidth size="small" sx={compactInput}>
                <InputLabel>VAT Type *</InputLabel>
                <Select 
                  value={vatType} 
                  onChange={(e) => handleVatTypeChange(e.target.value)} 
                  label="VAT Type *" 
                  sx={{ fontSize: '0.8rem', bgcolor: '#f3e8ff' }}
                >
                  <MenuItem value={1} sx={{ fontSize: '0.75rem', py: 0.5 }}>Excluding Tax</MenuItem>
                  <MenuItem value={2} sx={{ fontSize: '0.75rem', py: 0.5 }}>Including Tax</MenuItem>
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={6} sm={1.2}>
            <TextField fullWidth size="small" type="date" label="Clearing Date" value={formData.ClearingDate || ''} onChange={(e) => handleChange('ClearingDate', e.target.value)} disabled={isViewMode} InputLabelProps={{ shrink: true }} sx={compactInput} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: errors.purchaseDetails ? '#dc2626' : '#7c3aed' }}>Purchase Items *</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>({purchaseDetails.length} items)</Typography>
            {vatType === 2 && (
              <Typography variant="caption" sx={{ color: '#ca8a04', fontWeight: 600, bgcolor: '#fef9c3', px: 0.5, borderRadius: 0.5 }}>Tax Inclusive</Typography>
            )}
            {errors.purchaseDetails && (
              <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.7rem' }}>({errors.purchaseDetails})</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {purchaseDetails.length > 5 && (
              <Button 
                size="small" 
                onClick={() => setItemsModalOpen(true)} 
                startIcon={<Visibility sx={{ fontSize: 14 }} />}
                sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#7c3aed' }}
              >
                View All
              </Button>
            )}
            {!isViewMode && formData.MasterId && (
              <>
                <Button
                  size="small"
                  startIcon={<List sx={{ fontSize: 16 }} />}
                  onClick={handleOpenGetItemsModal}
                  variant="text"
                  sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#7c3aed' }}
                >
                  Get Items
                </Button>
                {/* <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addDetailRow} variant="outlined" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}>Add</Button> */}
              </>
            )}
          </Box>
        </Box>

        {/* Desktop Table View - Fixed height for 5 rows */}
        {!isMobile && (
          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 1.5, border: errors.purchaseDetails ? '1px solid #dc2626' : '1px solid #e5e7eb', borderRadius: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '4%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Sl No</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9d5ff', width: '15%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Item</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '6%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Rcvd Qty</th>
                  {isViewMode && (
                    <>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '6%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Dmg Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '6%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Excess Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '6%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Less Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '6%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Stock Qty</th>
                    </>
                  )}
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '8%' : '10%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Rate</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '6%' : '8%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Disc%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '8%' : '10%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Disc Amt</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '5%' : '6%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>VAT%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '8%' : '9%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>VAT Amt</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '9%' : '10%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Taxable</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9d5ff', width: isViewMode ? '9%' : '10%', background: 'transparent', color: '#6b21a8', fontWeight: 600 }}>Total</th>
                  {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e9d5ff', width: '4%', background: 'transparent' }}></th>}
                </tr>
              </thead>
              <tbody>
                {purchaseDetails.map((d, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#faf5ff' : '#ffffff' }}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #e9d5ff', textAlign: 'center', fontWeight: 600, color: '#7c3aed' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #e9d5ff', fontWeight: 600 }}>
                      <span style={{ fontWeight: 600, color: '#6b21a8' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || d.ItemName || '-'}</span>
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{d.ReceivedQuantity || 0}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.ReceivedQuantity ?? ''} onChange={(e) => handleDetailChange(index, 'ReceivedQuantity', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    {isViewMode && (
                      <>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #e9d5ff', textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>
                          {d.DamageQuantity || 0}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #e9d5ff', textAlign: 'center', fontWeight: 600, color: '#eab308' }}>
                          {d.ExcessQuantity || 0}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#f59e0b' }}>
                          {d.LessQuantity || 0}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#10b981' }}>
                          {(d.ReceivedQuantity || 0) - (d.DamageQuantity || 0) + (d.ExcessQuantity || 0) - (d.LessQuantity || 0)}
                        </td>
                      </>
                    )}
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{d.DiscountPercentage || 0}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.DiscountPercentage ?? 0} onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{formatCurrency(d.DiscountAmount)}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.DiscountAmount ?? 0} onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600 }}>{d.VATRate || 0}%</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.VATAmount || 0)}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {purchaseDetails.length === 0 && (
                  <tr><td colSpan={isViewMode ? 13 : 10} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No items added</td></tr>
                )}
              </tbody>
            </table>
          </Box>
        )}

        {/* Mobile Card View */}
        {isMobile && (
          <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
            {purchaseDetails.map((d, index) => (
              <Box key={index} sx={{ border: '1px solid #d1d5db', borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                    {items.find(i => i.Id === parseInt(d.ItemId))?.Name || 'Select Item'}
                  </Typography>
                  {!isViewMode && (
                    <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.5, bgcolor: '#fef2f2', color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  )}
                </Box>
                {!isViewMode && (
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Select Item</InputLabel>
                    <Select value={d.ItemId || ''} onChange={(e) => handleItemSelect(index, e.target.value)} label="Select Item" sx={{ fontSize: '0.85rem' }} MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}>
                      {items.map(item => <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>{item.Name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Batch</Typography>
                    <TextField size="small" value={d.BatchNumber || ''} onChange={(e) => handleDetailChange(index, 'BatchNumber', e.target.value)} disabled={isViewMode} fullWidth sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Rcvd Qty</Typography>
                    <TextField size="small" type="number" value={d.ReceivedQuantity || ''} onChange={(e) => handleDetailChange(index, 'ReceivedQuantity', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Rate</Typography>
                    <TextField size="small" type="number" value={d.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #f3f4f6' }}>
                  <Typography variant="caption" color="textSecondary">Total</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#eab308' }}>{formatCurrency(d.TotalAmount)}</Typography>
                </Box>
              </Box>
            ))}
            {purchaseDetails.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}><Typography variant="body2">No items added</Typography></Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Totals Section */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#7c3aed', mb: 1, display: 'block' }}>Additional Charges</Typography>
            <Box sx={{ bgcolor: '#faf5ff', p: 1.5, borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Freight" type="number" value={formData.FreightAmount || ''} onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Packing" type="number" value={formData.PackingAmount || ''} onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                {/* <Grid item xs={6}>
                  <TextField fullWidth size="small" label="TCS Amount" type="number" value={formData.TCSAmount || ''} onChange={(e) => handleChange('TCSAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid> */}
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Paid Amount" type="number" value={formData.PaidAmount || ''} onChange={(e) => handleChange('PaidAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} multiline rows={2} sx={compactInput} />
                </Grid>
                {/* Audit Info - under Remarks */}
                {(isViewMode || modalAction === 'edit') && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      Created by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#7c3aed' }}>{formData.CreatedByName || '-'}</Typography> on{' '}
                      <Typography component="span" variant="caption" sx={{ color: '#eab308' }}>
                        {formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      Modified by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#92400e' }}>{formData.ModifiedByName || '-'}</Typography> on{' '}
                      <Typography component="span" variant="caption" sx={{ color: '#92400e' }}>
                        {formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.ModifiedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                      </Typography>
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Summary</Typography>
            <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Subtotal</Typography><Typography variant="caption">{formatCurrency(formData.Amount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Discount</Typography><Typography variant="caption" color="error">-{formatCurrency(formData.DiscountAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Taxable</Typography><Typography variant="caption">{formatCurrency(formData.TaxableAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">VAT</Typography><Typography variant="caption">{formatCurrency(formData.VATAmount || 0)}</Typography></Box>
              {(parseFloat(formData.FreightAmount) || 0) > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Freight</Typography><Typography variant="caption">{formatCurrency(formData.FreightAmount)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Freight VAT ({formData.FreightVATRate || 0}%)</Typography><Typography variant="caption">{formatCurrency(formData.FreightVATAmount)}</Typography></Box>
                </>
              )}
              {(parseFloat(formData.PackingAmount) || 0) > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Packing</Typography><Typography variant="caption">{formatCurrency(formData.PackingAmount)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Packing VAT ({formData.PackingVATRate || 0}%)</Typography><Typography variant="caption">{formatCurrency(formData.PackingVATAmount)}</Typography></Box>
                </>
              )}
              {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">TCS</Typography><Typography variant="caption">{formatCurrency(formData.TCSAmount)}</Typography></Box> */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Round Off</Typography><Typography variant="caption">{formData.RoundOff >= 0 ? '+' : ''}{(formData.RoundOff || 0).toFixed(2)}</Typography></Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#eab308' }}>{formatCurrency(formData.TotalAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Paid</Typography><Typography variant="caption" sx={{ color: '#ca8a04' }}>{formatCurrency(formData.PaidAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Balance</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency(formData.BalanceAmount)}</Typography></Box>
            </Box>
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
        <Button onClick={handleCancelClick} variant="outlined" size="small">Cancel</Button>
        {isViewMode && formData.Id && !formData.IsConvertToGoodReceipt && (
          <Button 
            onClick={() => onToGoodReceipt && onToGoodReceipt(formData.Id, formData.BillNumber)} 
            variant="contained" 
            size="small" 
            disabled={loading}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
          >
            To Good Receipt
          </Button>
        )}
        {isViewMode && formData.Id && formData.IsConvertToGoodReceipt && !formData.IsVerified && (
          <Button 
            onClick={() => onVerify && onVerify(formData.Id)} 
            variant="contained" 
            size="small" 
            disabled={loading}
            sx={{ bgcolor: '#eab308', '&:hover': { bgcolor: '#ca8a04' } }}
          >
            Verify Purchase
          </Button>
        )}
        {!isViewMode && <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}>Save</Button>}
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog open={itemsModalOpen} onClose={() => setItemsModalOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '10px', maxHeight: '85vh' } }}>
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>All Purchase Items ({purchaseDetails.length})</Typography>
            </Box>
            <IconButton onClick={() => setItemsModalOpen(false)} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#1e40af' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>#</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: 600 }}>Item Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>HSN</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Rcvd Qty</th>
                  {isViewMode && (
                    <>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Dmg Qty</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Excess Qty</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Less Qty</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Stock Qty</th>
                    </>
                  )}
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>Rate</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Disc%</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>VAT%</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>VAT Amt</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>Taxable</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {purchaseDetails.map((d, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 500 }}>{index + 1}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500 }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || d.ItemName || '-'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.HSNCode || '-'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>{d.ReceivedQuantity || 0}</td>
                    {isViewMode && (
                      <>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>{d.DamageQuantity || 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#eab308' }}>{d.ExcessQuantity || 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#f59e0b' }}>{d.LessQuantity || 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#ca8a04' }}>
                          {(d.ReceivedQuantity || 0) - (d.DamageQuantity || 0) + (d.ExcessQuantity || 0) - (d.LessQuantity || 0)}
                        </td>
                      </>
                    )}
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(d.Rate)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.DiscountPercentage || 0}%</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.GSTRate || 0}%</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency((d.CGSTAmount || 0) + (d.SGSTAmount || 0) + (d.IGSTAmount || 0))}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(d.TotalAmount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot style={{ position: 'sticky', bottom: 0, background: '#f8fafc' }}>
                <tr style={{ borderTop: '2px solid #1e40af' }}>
                  <td colSpan="3" style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>Totals:</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700 }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0)}</td>
                  {isViewMode && (
                    <>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.DamageQuantity) || 0), 0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#eab308' }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ExcessQuantity) || 0), 0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#f59e0b' }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.LessQuantity) || 0), 0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#ca8a04' }}>{purchaseDetails.reduce((sum, d) => sum + ((d.ReceivedQuantity || 0) - (d.DamageQuantity || 0) + (d.ExcessQuantity || 0) - (d.LessQuantity || 0)), 0)}</td>
                    </>
                  )}
                  <td colSpan="3"></td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(purchaseDetails.reduce((sum, d) => sum + ((d.CGSTAmount || 0) + (d.SGSTAmount || 0) + (d.IGSTAmount || 0)), 0))}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0))}</td>
                </tr>
              </tfoot>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={() => setItemsModalOpen(false)} variant="outlined" size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              backgroundColor: '#fef3c7', 
              borderRadius: '50%', 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes that will be lost if you leave this page. 
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{ 
              borderColor: '#eab308', 
              color: '#eab308', 
              '&:hover': { borderColor: '#ca8a04', backgroundColor: '#fef9c3' } 
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Items Modal */}
      <Dialog
        open={vendorItemsModalOpen}
        onClose={handleCloseVendorItemsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ bgcolor: '#f8fafc', py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ListAlt sx={{ color: '#3b82f6', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>
                Vendor Items - {supplierDisplayName}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseVendorItemsModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingVendorItems ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              py: 8,
              px: 4,
              minHeight: 300
            }}>
              {/* Modern Skeleton Loading */}
              <Box sx={{ width: '100%', maxWidth: 600 }}>
                {/* Header Skeleton */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  mb: 1
                }}>
                  <Box sx={{ 
                    width: 150, 
                    height: 20, 
                    bgcolor: '#e2e8f0', 
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                  <Box sx={{ 
                    width: 80, 
                    height: 32, 
                    bgcolor: '#e2e8f0', 
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                </Box>

                {/* Table Header Skeleton */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  px: 2, 
                  py: 1.5,
                  bgcolor: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ width: 40, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ flex: 1, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 80, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 80, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 80, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 60, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                </Box>

                {/* Table Rows Skeleton */}
                {[1, 2, 3, 4].map((item, index) => (
                  <Box 
                    key={item}
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      px: 2, 
                      py: 2,
                      borderBottom: '1px solid #e2e8f0',
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      animationDelay: `${index * 0.1}s`,
                      '@keyframes shimmer': {
                        '0%': { opacity: 0.6 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.6 }
                      }
                    }}
                  >
                    <Box sx={{ 
                      width: 20, 
                      height: 20, 
                      bgcolor: '#e2e8f0', 
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{ 
                      flex: 1, 
                      height: 20, 
                      bgcolor: '#e2e8f0', 
                      borderRadius: 0.5 
                    }} />
                    <Box sx={{ 
                      width: 80, 
                      height: 20, 
                      bgcolor: '#e2e8f0', 
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{ 
                      width: 80, 
                      height: 20, 
                      bgcolor: '#e2e8f0', 
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{ 
                      width: 80, 
                      height: 20, 
                      bgcolor: '#e2e8f0', 
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{ 
                      width: 60, 
                      height: 20, 
                      bgcolor: '#e2e8f0', 
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                  </Box>
                ))}
              </Box>

              {/* Loading Text */}
              <Typography sx={{ 
                color: '#94a3b8', 
                fontSize: '0.875rem', 
                fontWeight: 500,
                mt: 3,
                animation: 'fadeInOut 1.5s ease-in-out infinite',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.5 },
                  '50%': { opacity: 1 }
                }
              }}>
                Loading vendor items...
              </Typography>
            </Box>
          ) : vendorItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8' }}>No items found for this vendor</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                  {selectedVendorItems.length} of {vendorItems.length} items selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllVendorItems}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    borderColor: '#cbd5e1',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      bgcolor: '#f1f5f9'
                    }
                  }}
                >
                  {selectedVendorItems.length === vendorItems.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: '#475569', fontSize: '0.7rem', borderBottom: '2px solid #cbd5e1', width: '40px' }}>
                        <Checkbox
                          checked={selectedVendorItems.length === vendorItems.length && vendorItems.length > 0}
                          indeterminate={selectedVendorItems.length > 0 && selectedVendorItems.length < vendorItems.length}
                          onChange={handleSelectAllVendorItems}
                          size="small"
                          sx={{ p: 0 }}
                        />
                      </th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #cbd5e1' }}>Item Name</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #cbd5e1', width: '90px' }}>HSN</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #cbd5e1', width: '90px' }}>Rate</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #cbd5e1', width: '90px' }}>Last Rate</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #cbd5e1', width: '70px' }}>VAT%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorItems.map((item, index) => {
                      const itemId = item.ItemId || item.Id;
                      const isSelected = selectedVendorItems.includes(itemId);
                      return (
                        <tr 
                          key={itemId}
                          onClick={() => handleToggleVendorItem(itemId)}
                          style={{ 
                            cursor: 'pointer',
                            background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#ffffff' : '#f8fafc'),
                            borderBottom: '1px solid #e2e8f0',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = '#f1f5f9';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#f8fafc';
                          }}
                        >
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0 }}
                            />
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '0.75rem', fontWeight: 500, color: '#1e293b' }}>
                            {item.ItemName || item.Name}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '0.7rem', color: '#64748b', textAlign: 'center' }}>
                            {item.HSNCode || item.HSN || '-'}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '0.75rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                            AED {(item.Rate || item.SaleRate || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '0.75rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                            AED {(item.LastPurchaseRateByVendor || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              backgroundColor: '#d1fae5',
                              color: '#065f46',
                              fontWeight: 600,
                              fontSize: '0.65rem'
                            }}>
                              {item.VATRate || item.GSTRate || item.GST || 0}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button 
            onClick={handleCloseVendorItemsModal} 
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddVendorItemsToPurchase}
            variant="contained"
            disabled={selectedVendorItems.length === 0}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.8rem',
              bgcolor: '#3b82f6', 
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': {
                bgcolor: '#cbd5e1',
                color: '#94a3b8'
              }
            }}
          >
            Add Selected Items ({selectedVendorItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            m: isMobile ? 0 : 1,
            maxHeight: '80vh',
            width: isMobile ? '100%' : '85%',
            maxWidth: '1100px',
            minWidth: '850px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 0.75,
          px: 1.5,
          borderBottom: '1px solid #d1d5db',
          background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
          minHeight: '40px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <List sx={{ color: '#7c3aed', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b21a8', fontSize: '0.85rem' }}>
                Get Items ({getFilteredItems().filter(item => !isItemAlreadyInPurchase(item.Id)).length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#6b21a8', p: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280', fontSize: '0.85rem' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: getItemsSearchValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleGetItemsSearch('')}
                          sx={{ p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fde68a' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#ca8a04', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 8).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                    {selectedItems.length > 8 && (
                      <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table - Desktop */}
              {!isMobile && (
                <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', height: '32px' }}>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#6b21a8', width: '4%', fontSize: '0.65rem' }}>
                            <Checkbox
                              size="small"
                              checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                              indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  getPaginatedItems().forEach(item => {
                                    if (!selectedItems.find(si => si.Id === item.Id)) {
                                      handleItemSelection(item, true);
                                    }
                                  });
                                } else {
                                  getPaginatedItems().forEach(item => {
                                    handleItemSelection(item, false);
                                  });
                                }
                              }}
                              sx={{ color: '#6b21a8', '&.Mui-checked': { color: '#6b21a8' }, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                            />
                          </th>
                          <th style={{ padding: '4px 4px', textAlign: 'left', fontWeight: 600, color: '#6b21a8', width: '28%', fontSize: '0.65rem' }}>Item</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#6b21a8', width: '8%', fontSize: '0.65rem' }}>HSN</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#6b21a8', width: '6%', fontSize: '0.65rem' }}>VAT%</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#6b21a8', width: '8%', fontSize: '0.65rem' }}>Stock</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#6b21a8', width: '7%', fontSize: '0.65rem' }}>Qty</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#6b21a8', width: '10%', fontSize: '0.65rem' }}>Rate</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#6b21a8', width: '6%', fontSize: '0.65rem' }}>Disc%</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#6b21a8', width: '9%', fontSize: '0.65rem' }}>Disc Amt</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#6b21a8', width: '12%', fontSize: '0.65rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedItems().map((item, index) => {
                          const selectedItem = selectedItems.find(si => si.Id === item.Id);
                          const isSelected = !!selectedItem;

                          const backgroundColor = isSelected ? '#fef3c7' : (index % 2 === 0 ? '#faf5ff' : '#fff');

                          const quantity = isSelected ? parseFloat(selectedItem.selectedQuantity) || 0 : 0;
                          const rate = isSelected ? parseFloat(selectedItem.selectedRate) || 0 : (item.Rate || item.SaleRate || 0);
                          const discountPct = isSelected ? parseFloat(selectedItem.selectedDiscountPercentage) || 0 : 0;
                          const discountAmt = isSelected ? parseFloat(selectedItem.selectedDiscountAmount) || 0 : 0;
                          const amount = quantity * rate;
                          const finalDiscountAmount = discountAmt > 0 ? discountAmt : (amount * discountPct) / 100;
                          const total = amount - finalDiscountAmount;

                          return (
                            <tr key={item.Id} style={{
                              borderBottom: '1px solid #e5e7eb',
                              background: backgroundColor
                            }}>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={(e) => handleItemSelection(item, e.target.checked)}
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                />
                              </td>
                              <td style={{ padding: '3px 8px', fontWeight: 500, fontSize: '0.75rem' }}>
                                {item.Name}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#7c3aed' }}>{item.VATRate || 0}%</td>
                              <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', color: item.BalanceStock > 0 ? '#059669' : '#dc2626' }}>
                                {item.BalanceStock || 0}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedQuantity}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                    inputProps={{ min: 1 }}
                                    sx={{
                                      width: 50,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'center',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedRate}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                      width: 65,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'right',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>
                                    AED {(item.Rate || item.SaleRate || 0).toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedDiscountPercentage}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{
                                      width: 45,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'center',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>0</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedDiscountAmount}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                      width: 60,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'right',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>0.00</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', color: '#7c3aed' }}>
                                {isSelected ? `AED ${total.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                  Showing {((currentItemsPage - 1) * itemsPerPage) + 1} to {Math.min(currentItemsPage * itemsPerPage, getFilteredItems().filter(item => !isItemAlreadyInPurchase(item.Id)).length)} of {getFilteredItems().filter(item => !isItemAlreadyInPurchase(item.Id)).length} items
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => setCurrentItemsPage(1)}
                    disabled={currentItemsPage === 1}
                    sx={{ minWidth: 32, px: 0.5, fontSize: '0.7rem' }}
                  >
                    First
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                    disabled={currentItemsPage === 1}
                    sx={{ minWidth: 32, px: 0.5, fontSize: '0.7rem' }}
                  >
                    Prev
                  </Button>
                  <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: '#f3e8ff', borderRadius: 0.5, fontSize: '0.7rem' }}>
                    {currentItemsPage} / {getTotalPages}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setCurrentItemsPage(prev => Math.min(getTotalPages, prev + 1))}
                    disabled={currentItemsPage === getTotalPages}
                    sx={{ minWidth: 32, px: 0.5, fontSize: '0.7rem' }}
                  >
                    Next
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setCurrentItemsPage(getTotalPages)}
                    disabled={currentItemsPage === getTotalPages}
                    sx={{ minWidth: 32, px: 0.5, fontSize: '0.7rem' }}
                  >
                    Last
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button 
            onClick={handleCloseGetItemsModal} 
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.8rem',
              bgcolor: '#7c3aed', 
              '&:hover': { bgcolor: '#6d28d9' },
              '&:disabled': {
                bgcolor: '#cbd5e1',
                color: '#94a3b8'
              }
            }}
          >
            {addingItemsLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Adding...
              </>
            ) : (
              `Add Selected Items (${selectedItems.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PurchaseModal;
